import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { cn } from '../../lib/utils';

/**
 * Dates in this app travel as `YYYY-MM-DD` strings — that is what the filter
 * values hold and what the API takes. Parsing them with `new Date(string)`
 * treats them as UTC midnight, which lands on the previous day for anyone west
 * of Greenwich, so both directions are done by hand against local time.
 */
export const toISODate = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

export const fromISODate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return undefined;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export interface CalendarProps {
  /** Start of the selected range, if one has been picked. */
  from?: Date;
  /** End of the selected range. Absent while only one end is chosen. */
  to?: Date;
  /** The left-hand month on show. Controlled, so a preset can jump to its month. */
  month: Date;
  onMonthChange: (_month: Date) => void;
  /** A day was clicked. The caller decides whether it starts or ends the range. */
  onSelect: (_date: Date) => void;
  /** Days after this are not selectable — a "created" filter has no future. */
  maxDate?: Date;
  /** Two months side by side by default. */
  numberOfMonths?: number;
  className?: string;
}

/**
 * Two months of days you can click, with the selected range drawn across them.
 *
 * Built on react-day-picker rather than by hand. The version before this was
 * mine — about a hundred and eighty lines of date arithmetic — and it was
 * raised in review: "these things are kind of fraught with issues if you've
 * just baked your own." That is the right instinct. A calendar has to get
 * month lengths, leap years, week starts, locales, keyboard movement and
 * screen-reader announcement right, and each of those is a place to be quietly
 * wrong in a way no one notices until a seller in another timezone loses a day.
 *
 * The admin app already has a dual-calendar range picker, which is where the
 * two-month shape comes from — but it is Polaris `DatePicker` with
 * `multiMonth`, and partner-web has no Polaris, so the pattern ports and the
 * engine cannot.
 *
 * The props are unchanged from the hand-written version, so no caller moved:
 * a range is still two dates, and a click is still a single day handed back
 * for the caller to interpret.
 */
export function Calendar({
  from,
  to,
  month,
  onMonthChange,
  onSelect,
  maxDate,
  numberOfMonths = 2,
  className,
}: CalendarProps) {
  const selected: DateRange | undefined = from ? { from, to: to ?? from } : undefined;

  return (
    <DayPicker
      mode="range"
      selected={selected}
      month={month}
      onMonthChange={onMonthChange}
      numberOfMonths={numberOfMonths}
      disabled={maxDate ? { after: maxDate } : undefined}
      // Paging forward stops at the last month that has any selectable day in
      // it, rather than running on into months where every date is disabled.
      endMonth={maxDate}
      showOutsideDays={false}
      className={cn('relative', className)}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left' ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
        // The three overrides exist to keep the handles the tests hold: days
        // are addressed by their date rather than their position in a grid,
        // which survives paging, month lengths and a second month appearing.
        PreviousMonthButton: (props) => (
          <button {...props} data-testid="calendar-prev" aria-label="Previous month" />
        ),
        NextMonthButton: (props) => (
          <button {...props} data-testid="calendar-next" aria-label="Next month" />
        ),
        DayButton: ({ day, modifiers, ...props }) => (
          <button
            {...props}
            data-testid={`calendar-day-${toISODate(day.date)}`}
            aria-pressed={!!modifiers.range_start || !!modifiers.range_end}
          />
        ),
      }}
      // Styled with tokens rather than the library's stylesheet, so the
      // calendar follows the shop's theme like every other control.
      classNames={{
        months: 'flex gap-5',
        month: 'w-[15.5rem] space-y-1',
        month_caption: 'flex h-7 items-center justify-center',
        caption_label: 'text-xs font-semibold text-foreground',
        nav: 'absolute inset-x-0 top-0 z-10 flex items-center justify-between',
        button_previous:
          'rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40',
        button_next:
          'rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40',
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-[2.214rem] py-1 text-center text-[10px] font-medium uppercase text-muted-foreground',
        week: 'flex',
        day: 'h-7 w-[2.214rem] p-0 text-center',
        day_button:
          'h-7 w-full rounded-md text-xs tabular-nums text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:text-muted-foreground/40 disabled:hover:bg-transparent',
        today: 'font-semibold',
        // The days in between are a continuous band, so the ends are rounded
        // and the middle is not.
        range_middle:
          '[&>button]:rounded-none [&>button]:bg-primary/10 [&>button]:text-primary [&>button]:hover:bg-primary/10',
        range_start:
          '[&>button]:rounded-r-none [&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
        range_end:
          '[&>button]:rounded-l-none [&>button]:bg-primary [&>button]:font-semibold [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
        hidden: 'invisible',
      }}
      formatters={{
        formatCaption: (date) =>
          date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
      }}
      // The caller owns what a click means — first click starts the range,
      // second closes it, a click before the start moves the start. Taking
      // react-day-picker's own range handling would move that decision in here
      // and change behaviour the filter editor's tests pin.
      onDayClick={(day, modifiers) => {
        if (modifiers.disabled) return;
        onSelect(day);
      }}
    />
  );
}
